interface FileTypeInfo {
  mime: string;
  description: string;
}

function getFileTypeInfo(filename: string): FileTypeInfo {
  const ext = filename.split('.').pop()?.toLowerCase();

  const typeMap: Record<string, FileTypeInfo> = {
    xlsx: {
      mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      description: 'Excel Spreadsheet',
    },
    xls: { mime: 'application/vnd.ms-excel', description: 'Excel 97-2003 Spreadsheet' },
    xlsm: { mime: 'application/vnd.ms-excel.sheet.macroEnabled.12', description: 'Excel Macro-Enabled Spreadsheet' },
    csv: { mime: 'text/csv', description: 'CSV File' },
    pdf: { mime: 'application/pdf', description: 'PDF Document' },
    zip: { mime: 'application/zip', description: 'ZIP Archive' },
    docx: {
      mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      description: 'Word Document',
    },
    doc: { mime: 'application/msword', description: 'Word 97-2003 Document' },
    pptx: {
      mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      description: 'PowerPoint Presentation',
    },
    ppt: { mime: 'application/vnd.ms-powerpoint', description: 'PowerPoint 97-2003 Presentation' },
    txt: { mime: 'text/plain', description: 'Text File' },
    json: { mime: 'application/json', description: 'JSON File' },
    xml: { mime: 'application/xml', description: 'XML File' },
    png: { mime: 'image/png', description: 'PNG Image' },
    jpg: { mime: 'image/jpeg', description: 'JPEG Image' },
    jpeg: { mime: 'image/jpeg', description: 'JPEG Image' },
    gif: { mime: 'image/gif', description: 'GIF Image' },
    svg: { mime: 'image/svg+xml', description: 'SVG Image' },
    mp4: { mime: 'video/mp4', description: 'MP4 Video' },
    mp3: { mime: 'audio/mpeg', description: 'MP3 Audio' },
  };

  return (ext ? typeMap[ext] : undefined) ?? { mime: 'application/octet-stream', description: 'File' };
}

async function streamDownloader(
  directBackendUrl: string,
  suggestedName: string,
  token: string,
  windowRef: Window & typeof globalThis,
): Promise<{ notAuth?: boolean; status?: number }> {
  if (!('showSaveFilePicker' in windowRef)) {
    try {
      const { notAuth, status } = await fallbackDownload(directBackendUrl, suggestedName, token);
      if (notAuth) return { notAuth, status };
    } catch (err) {
      console.error('Error en fallbackDownload:', err);
      console.error();
      throw err;
    }
  }

  const { mime, description } = getFileTypeInfo(suggestedName);
  const ext = suggestedName.split('.').pop()?.toLowerCase();

  const fileHandle = await (windowRef as any).showSaveFilePicker({
    suggestedName,
    excludeAcceptAllOption: true,
    types: [
      {
        description,
        accept: { [mime]: ext ? [`.${ext}`] : [] },
      },
    ],
  });

  const writable = await fileHandle.createWritable();
  try {
    const response = await fetch(directBackendUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 401 || response.status === 403) {
      return { notAuth: true, status: response.status };
    }
    if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);
    if (!response.body) throw new Error('El cuerpo de la respuesta está vacío');

    const reader = response.body.getReader();

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      await writable.write(value);
    }

    await writable.close();
    return { status: 200 };
  } catch (streamErr) {
    await writable.abort();
    throw streamErr;
  }
}

async function fallbackDownload(
  url: string,
  name: string,
  token: string,
): Promise<{ notAuth?: boolean; status?: number }> {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 401 || res.status === 403) {
    return { notAuth: true, status: res.status };
  }

  if (!res.ok) return { status: res.status };

  const blob = await res.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = downloadUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();

  window.URL.revokeObjectURL(downloadUrl);
  document.body.removeChild(a);

  return { status: 200 };
}

export { streamDownloader };
